import { ChangeDetectionStrategy, Component, HostBinding, input } from '@angular/core';

import { PayoutType as MyBetsPayoutType, SlipState } from '@bpos/v1/my-bets';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CrmTokensMessages, CrmTokensMyBets, LinearRewardTokens } from '@frontend/sports/types/components/sitecore';
import { CRMTokenImage } from '@frontend/sports/types/models/crm-tokens';
import { isEdsPromoTokenBetAndGet } from 'packages/sports/common/betslip/modules/reward-tokens/services/reward-tokens.utils';

import { ITokenSignposting } from '../../../crm-postbet-signposting/postbet-signposting.service';
import { TokenType } from '../../../my-bets-base/models/my-bets-viewmodels';
import { PayoutType, RewardTokenType } from '../../../tokens-base/token-base.models';
import { MyBetsBaseUsedRewardTokenComponent } from './my-bets-base-used-reward-token.component';

@Component({
    selector: 'ms-my-bets-used-reward-token-epcot',
    templateUrl: 'my-bets-used-reward-token-epcot.component.html',
    styleUrl: './my-bets-used-reward-token-epcot.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBetsUsedRewardTokenEpcotComponent extends MyBetsBaseUsedRewardTokenComponent {
    @HostBinding('class') className = 'my-bets-used-reward-token my-bets-used-reward-token--ecpot';
    tokenSignPost = input<ITokenSignposting | undefined>();
    rewardTokenImage = input<CRMTokenImage | undefined>();
    enableRedesignedBetCard = this.myBetsUtilService.isBetCardRedesigned();

    private readonly translationMessages: CrmTokensMessages = this.sitecore.crmTokens.messages;
    private readonly linearMessages: LinearRewardTokens = this.sitecore.crmTokens.linearRewardTokens;
    private readonly translationMyBetsMessages: CrmTokensMyBets = this.sitecore.crmTokens.myBets;

    // private earlyPayoutUtils: EarlyPayoutUtils, // private accaBoostConfig: AccaBoostConfig, // private currencyPipe: CurrencyPipe, // private crmConfig: CrmConfig,

    get tokensTitle(): string {
        const acquisitionRewardTitle = this.getRewardTargetDescription(this.betslip.placeBetSignPosting?.rewardTargetType);

        if (acquisitionRewardTitle) {
            return acquisitionRewardTitle;
        }

        if (isEdsPromoTokenBetAndGet(this.betslip.edsPromoTokens)) {
            return this.translationMessages.BetAndGet;
        }

        switch (this.betslip.usedRewardToken?.tokenType) {
            case TokenType.OddsBoostToken: {
                return this.translationMessages.OddsBoost;
            }
            case TokenType.RiskFreeToken: {
                return this.translationMessages.RiskFree;
            }
            case TokenType.AccaBoostToken: {
                return this.translationMyBetsMessages.AccaBoostUsed;
            }
            case TokenType.FreeBetToken: {
                return this.getFreeBetTitle();
            }
            default:
                return '';
        }
    }

    get hasDetails(): boolean {
        const tokenType = this.betslip.usedRewardToken?.tokenType;

        return tokenType === TokenType.RiskFreeToken || isEdsPromoTokenBetAndGet(this.betslip.edsPromoTokens);
    }

    get detailsText(): string {
        if (isEdsPromoTokenBetAndGet(this.betslip.edsPromoTokens)) {
            return this.getBetAndGetDetails();
        }

        const token = this.betslip.usedRewardToken;

        if (!token || token.tokenType !== TokenType.RiskFreeToken) {
            return '';
        }

        return this.getRiskFreePayoutDetails();
    }

    private getFreeBetTitle(): string {
        const message = this.translationMessages.Freebet;

        const freeBetValue = this.moneyService.transform(this.betslip.stake.value, true, 2, true, undefined, undefined, false);

        return `${freeBetValue} ${message}`;
    }

    private getBetAndGetDetails(): string {
        const edsToken = this.betslip.edsPromoTokens[0];

        const payoutType = edsToken.payoutType;

        let mappedPayoutType: PayoutType | undefined;

        switch (payoutType) {
            case MyBetsPayoutType.Bonus:
                mappedPayoutType = PayoutType.Bonus;
                break;

            case MyBetsPayoutType.Cash:
                mappedPayoutType = PayoutType.Cash;
                break;

            case MyBetsPayoutType.FreeBet:
                mappedPayoutType = PayoutType.FreeBet;
                break;
        }

        if (!mappedPayoutType) {
            return '';
        }

        const displayPayoutType = this.tokensSummaryService.getDisplayPayoutType(mappedPayoutType, RewardTokenType.RiskFreeBet);

        return interpolateDoubleBracesString(
            this.enableRedesignedBetCard ? this.linearMessages.MyBetsBetAndGetPromoDetailsTemplate : this.linearMessages.MyBetsBetAndGetDetails,
            {
                payoutType: displayPayoutType,
                amount: '',
            },
        );
    }

    getRiskFreeRewardTemplate(isIncremental: boolean): string {
        if (this.enableRedesignedBetCard) {
            return this.riskFreeRewardsTemplateForRedesign(isIncremental);
        } else {
            if (this.betslip.state === SlipState.Lost) {
                return isIncremental
                    ? this.translationMyBetsMessages.RiskFreeIncrementalAwardedTemplate
                    : this.translationMyBetsMessages.RiskFreeUsedAwardedTemplate;
            } else {
                return isIncremental ? this.linearMessages.MyBetsRiskFreeIncrementalDetails : this.linearMessages.MyBetsRiskFreeDetails;
            }
        }
    }

    riskFreeRewardsTemplateForRedesign(isIncremental: boolean): string {
        if (this.betslip.state === SlipState.Lost) {
            return isIncremental
                ? this.translationMyBetsMessages.RiskFreeIncrementalAwardedTemplate
                : this.translationMyBetsMessages.RiskFreePromoUsedAwardedTemplate;
        } else {
            return isIncremental
                ? this.linearMessages.MyBetsRiskFreeIncrementalDetailsTemplate
                : this.linearMessages.MyBetsRiskFreePromoDetailsTemplate;
        }
    }
}
